<template>
    <section class="post-operations-section-wrapper">
        <div class="section-header">
            <h4>{{ $t("message.postOperations") }}</h4>
        </div>

        <div v-if="postOperations.length" class="post-operations-section">
            <div
                v-for="postOp in postOperations"
                :key="postOp.id"
                class="post-op-row"
                @click="handlePostOpClick(postOp)"
            >
                <div class="post-op-info">
                    <div class="post-op-status">
                        <i class="material-icons">event_available</i>
                        <span>{{ postOp.Operation_Statu }}</span>
                    </div>
                    <div class="post-op-owner">
                        <i class="material-icons">person</i>
                        <span>{{ $_.get(postOp, "Owner.name", "") }}</span>
                    </div>
                    <div class="post-op-date">
                        <i class="material-icons">event</i>
                        <span>{{ postOp.Operation_Date }}</span>
                    </div>
                    <div class="post-op-contact">
                        <i class="material-icons">contacts</i>
                        <span>{{ $_.get(postOp, "Contact.name", "") }}</span>
                    </div>
                </div>
                <SupsisButton
                    class="view-post-op-btn"
                    icon="open_in_new"
                    @click.stop="gotoPostOperationOnZoho(postOp.id)"
                />
                <a-divider />
            </div>
        </div>

        <div v-else-if="loading" class="loading-container">
            <span>{{ $t("message.loadingPostOperations") }}</span>
            <BucketSpinner class="large-spinner" />
        </div>

        <div v-else class="empty-post-ops-container">
            <span class="material-icons empty-icon">event_busy</span>
            <p class="empty-text">{{ $t("message.noPostOperations") }}</p>
        </div>

        <Modal2
            v-if="selectedPostOp"
            :visible="!!selectedPostOp"
            title="$t('message.postOperationDetails')"
            :footer="null"
            @cancel="handleModalClose"
            className="post-op-viewer-modal"
        >
            <PostOpViewer :postOp="selectedPostOp" />
        </Modal2>
    </section>
</template>

<script>
import Vue from "vue";
import { mapState } from "vuex";
import BucketSpinner from "@/views/Reports/common/spinners/BucketSpinner.vue";
import SupsisButton from "../../../../views/Pages/Settings/components/SupsisButton.vue";
import { ZOHO_API } from "../../../../api/zoho";
import PostOpViewer from "./PostOpViewer.vue";
import Modal2 from "../../../common/Modal2.vue";

export default Vue.extend({
    name: "ZohoPostOperationsSection",
    components: {
        BucketSpinner,
        SupsisButton,
        PostOpViewer,
        Modal2,
    },
    props: {
        contact: {
            type: Object,
            required: true,
        },
    },
    data() {
        return {
            postOperations: [],
            loading: false,
            selectedPostOp: null,
        };
    },
    async created() {
        await this.fetchPostOperations();
    },
    computed: {
        ...mapState({
            zohoChannelExists: (state) => !!state.channel.channels.find((x) => x.type === "zoho"),
            zohoChannel: (state) => state.channel.channels.find((x) => x.type === "zoho"),
        }),
    },
    methods: {
        async fetchPostOperations() {
            this.loading = true;
            try {
                const contactId = this.contact._id;
                const zohoContactId = this.contact.zohoInfo?.contactId;
                const phone = this.contact.whatsappNumber || this.contact.phone;
                const postOperations =
                    zohoContactId
                        ? await ZOHO_API.searchRecordsByContactId("Post_Operations", zohoContactId)
                        : await ZOHO_API.searchRecordsByPhone("Post_Operations", phone);
                if (this.contact?._id === contactId) {
                    this.postOperations = Array.isArray(postOperations) ? postOperations : [];
                }
            } catch (error) {
                console.error("Post operasyonlar yüklenirken hata:", error);
            } finally {
                this.loading = false;
            }
        },
        formatDate(dateString) {
            if (!dateString) return "";
            return new Date(dateString).toLocaleDateString("tr-TR", {
                year: "numeric",
                month: "long",
                day: "numeric",
            });
        },
        gotoPostOperationOnZoho(postOpId) {
            const orgId = "org645396914";
            if (orgId && postOpId) {
                window.open(`https://crm.zoho.com/crm/${orgId}/tab/CustomModule5/${postOpId}`, "_blank");
            }
        },
        handlePostOpClick(postOp) {
            this.selectedPostOp = postOp;
        },
        handleModalClose() {
            this.selectedPostOp = null;
        },
    },
    watch: {
        contact: {
            handler() {
                this.postOperations = [];
                this.fetchPostOperations();
            },
            deep: true,
        },
    },
});
</script>

<style scoped lang="scss">
.post-operations-section-wrapper {
    margin-top: 20px;

    .section-header {
        margin-bottom: 12px;
        padding-bottom: 6px;
        border-bottom: 1px solid #eee;

        h4 {
            font-size: 16px;
            font-weight: 600;
            color: #2d3748;
            margin: 0;
        }
    }

    .post-operations-section {
        .post-op-row {
            position: relative;
            padding: 12px;
            border: 1px solid #eee;
            border-radius: 6px;
            margin-bottom: 12px;
            cursor: pointer;
            transition: background-color 0.3s;

            &:hover {
                background-color: #f7fafc;
            }

            .post-op-info {
                .post-op-status,
                .post-op-owner,
                .post-op-date,
                .post-op-contact {
                    display: flex;
                    align-items: center;
                    gap: 6px;
                    margin-bottom: 4px;

                    i.material-icons {
                        font-size: 18px;
                        color: #64748b;
                    }
                }

                .post-op-status {
                    font-weight: 500;
                    font-size: 15px;
                    color: #2d3748;
                }
            }

            .view-post-op-btn {
                position: absolute;
                top: 12px;
                right: 12px;
            }
        }
    }

    .loading-container,
    .empty-post-ops-container {
        min-height: 100px;
        display: flex;
        flex-direction: column;
        justify-content: center;
        align-items: center;
        padding: 20px;
        color: #94a3b8;

        .empty-icon {
            font-size: 36px;
            margin-bottom: 8px;
        }

        .empty-text {
            font-size: 14px;
            text-align: center;
            margin: 0;
        }
    }
}

.post-op-viewer-modal {
    .ant-modal {
        width: 700px !important;
    }
}
</style>
<style lang="scss">
.post-op-viewer-modal {
    .ant-modal {
        width: 700px !important;
        max-width: 80vw;
    }
}
</style>
